import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Thread } from './chat/Thread';
import { useCustomLangGraphRuntime } from '../services/langGraphRuntime';
import { shortid } from '../services/id.service';
import { ConversationHistory } from './chat/ConversationHistory';

interface TechOnboarderProps {
  initialQuestion?: string;
  showHistory?: boolean;
  conversationId?: string;
}

const AssistantWrapper = ({
  threadId,
  initialQuestion,
}: {
  threadId: string;
  initialQuestion: string;
}) => {
  const { runtime, isHistoricalThread, isLoaded, resumeGraph } = useCustomLangGraphRuntime({
    agentType: 'tech_onboarder',
    model: 'claude-4-sonnet',
    threadId,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const hasResumedOAuth = useRef(false);

  // Handle OAuth return for connector authorization
  useEffect(() => {
    if (!isLoaded || hasResumedOAuth.current) return;

    const authorized = searchParams.get('authorized');
    const sourceId = searchParams.get('source_id');

    if (authorized === 'true' && sourceId) {
      hasResumedOAuth.current = true;
      setSearchParams({}, { replace: true });
      resumeGraph({
        status: 'authorized',
        source_id: sourceId,
      });
    }
  }, [isLoaded, searchParams, setSearchParams, resumeGraph]);

  const autoRunEnabled = !isHistoricalThread;

  if (isHistoricalThread && !isLoaded) {
    return (
      <div className="p-4">
        <p>Loading conversation...</p>
      </div>
    );
  }

  return (
    <Thread
      runtime={runtime}
      initialQuestion={initialQuestion}
      toolCallHandlers={{
        onResumeGraph: resumeGraph,
        threadId,
      }}
      autoRunCharts={autoRunEnabled}
      emptyStateMessage="Hi! I'm here to help you get your data connected and organized. Let's start by learning about your business and what data sources you work with."
    />
  );
};

export const TechOnboarder: React.FC<TechOnboarderProps> = ({
  initialQuestion = '',
  showHistory = false,
  conversationId,
}) => {
  const defaultThreadId = useMemo(() => shortid(), []);
  const currentThreadId = conversationId || defaultThreadId;

  const content = (
    <AssistantWrapper
      key={currentThreadId}
      threadId={currentThreadId}
      initialQuestion={initialQuestion}
    />
  );

  if (showHistory) {
    return (
      <div className="h-full w-full flex">
        <div className="flex-[3] min-w-0">
          {content}
        </div>
        <div className="flex-1 min-w-0">
          <ConversationHistory
            activeThreadId={currentThreadId}
            agentType="tech_onboarder"
            baseUrl="/onboarding"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      {content}
    </div>
  );
};
